import classnames from 'classnames';
import React, { PropTypes } from 'react';
import { KeyHelpers } from 'es6/core/keys';
import { AppPaths } from 'es6/core/paths';
import AggregateMemory from 'es6/models/aggregate-memory';
import Goal from 'es6/models/goal';
import { t } from 'es6/services/localization';
import { ProgressBar, ProgressBarSentenceTrainer } from 'es6/components/common/progress-bar';
import StudyAllRibbon from 'es6/components/apps/study-all-ribbon';

const APP_CHOICE_SELECTOR = '.app-choice:not(.disabled)';

const ItemProgress = ({ aggregateMemory }) => (
  <div className="header-progress">
    <div className="progress-label">
      <span className="progress">{aggregateMemory.items.progress}%</span>
    </div>
    <ProgressBar progress={aggregateMemory.items.progress} />
  </div>
);

ItemProgress.propTypes = {
  aggregateMemory: PropTypes.instanceOf(AggregateMemory).isRequired,
};

const AppChoice = ({ appName, onClick, glyphClass, title, description, disabled = false }) => {
  const href = disabled ? null : '#';
  const className = classnames('app-choice', { disabled });
  const glyphClassName = classnames('glyph', glyphClass, 'app-glyph');

  const handleClick = (e) => {
    e.preventDefault();

    if (!disabled) {
      onClick(appName);
    }
  };

  return (
    <li className="app-choice-container">
      <a href={href} onClick={handleClick} className={className}>
        <span className={glyphClassName} />

        <div className="app-details">
          <span className="app-title">{title}</span>
          <span className="app-description">{description}</span>
        </div>

        <div className="app-launcher-container">
          <span className="glyph glyph-triangle-right app-launcher" />
        </div>
      </a>
    </li>
  );
};

AppChoice.propTypes = {
  appName: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  glyphClass: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  title: PropTypes.string.isRequired,
};

/**
 * Renders sentence trainer progress.
 *
 * @this AppSelection
 * @returns {ReactElement}
 */
const SentenceProgress = ({ aggregateMemory }) => {
  const levelLabel = t('st-level-{level}', {
    level: aggregateMemory.sentences.level,
  });

  return (
    <div className="header-progress">
      <div className="progress-label">
        <span className="level-label">{levelLabel}</span>
      </div>
      <ProgressBarSentenceTrainer progress={aggregateMemory.sentences.progress} />
    </div>
  );
};

SentenceProgress.propTypes = {
  aggregateMemory: PropTypes.instanceOf(AggregateMemory).isRequired,
};

class AppSelection extends React.Component {
  constructor(...args) {
    super(...args);

    this.handleKeydown = (e) => {
      const appChoices = [...this.refs.applicationGroups.querySelectorAll(APP_CHOICE_SELECTOR)];
      const focusedIndex = appChoices.indexOf(document.activeElement);
      let newIndex = focusedIndex;

      if (KeyHelpers.isUp(e.keyCode)) {
        newIndex--;

        if (newIndex < 0) {
          newIndex = appChoices.length - 1;
        }
      } else if (KeyHelpers.isDown(e.keyCode)) {
        newIndex++;

        if (newIndex >= appChoices.length) {
          newIndex = 0;
        }
      } else {
        return;
      }

      appChoices[newIndex].focus();
    };
  }

  /**
   * Focus the first app choice and set up an event to listen to for
   * other app choices.
   */
  componentDidMount() {
    this.refs.applicationGroups.querySelector(APP_CHOICE_SELECTOR).focus();
    document.addEventListener('keydown', this.handleKeydown);
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeydown);
  }

  render() {
    const { aggregateMemory, onAppSelection, isStudyAll, goal } = this.props;
    const sentencesDisabled = goal ? !goal.isSentenceStudyEnabled() : false;

    return (
      <div className="screen screen-app-selection">
        {isStudyAll ? <StudyAllRibbon /> : null}

        <div className="screen-content">
          <div ref="applicationGroups" className="application-groups">
            <section className="application-group application-group-items">
              <header className="application-group-header">
                <span className="title">{t('iknow.label')}</span>

                {aggregateMemory && <ItemProgress aggregateMemory={aggregateMemory} />}
              </header>

              <ul className="application-list">
                <AppChoice
                  appName={AppPaths.IKNOW}
                  glyphClass="glyph-app-logo-iknow"
                  title={t('iknow-app-title')}
                  description={t('iknow-app-description')}
                  onClick={onAppSelection}
                />

                <AppChoice
                  appName={AppPaths.RAPID_CHOICE}
                  glyphClass="glyph-app-logo-rapid-choice"
                  title={t('rapid-choice-app-title')}
                  description={t('rapid-choice-app-description')}
                  onClick={onAppSelection}
                />

                <AppChoice
                  appName={AppPaths.SELF_ASSESSMENT}
                  glyphClass="glyph-app-logo-self-assessment"
                  title={t('self-assessment-app-title')}
                  description={t('self-assessment-app-description')}
                  onClick={onAppSelection}
                />
              </ul>
            </section>

            <section className="application-group application-group-sentences">
              <header className="application-group-header">
                <span className="title">{t('app-branding.sentence-trainer')}</span>

                {aggregateMemory && !sentencesDisabled &&
                  <SentenceProgress aggregateMemory={aggregateMemory} />}
              </header>

              <ul className="app-choices">
                <AppChoice
                  appName={AppPaths.SENTENCE_TRAINER}
                  glyphClass="glyph-app-logo-sentence-trainer"
                  title={t('sentence-trainer-app-title')}
                  description={t('sentence-trainer-app-description')}
                  onClick={onAppSelection}
                  disabled={sentencesDisabled}
                />
              </ul>
            </section>
          </div>
        </div>
      </div>
    );
  }
}

AppSelection.propTypes = {
  aggregateMemory: React.PropTypes.object,
  goal: React.PropTypes.object,
  onAppSelection: React.PropTypes.func.isRequired,
  isStudyAll: React.PropTypes.bool,
};

AppSelection.defaultProps = {
  aggregateMemory: null,
  goal: null,
  isStudyAll: false,
};

// The screen banner will be rendered at a different location in the DOM, so
// export a stateless component that the controller can use to render it.
export function AppSelectionBanner(props) {
  const bannerMessage = props.goal ? props.goal.title : t('choose-an-app-study-all');

  return (
    <div className="screen-app-selection-banner">{bannerMessage}</div>
  );
}

AppSelectionBanner.propTypes = {
  goal: PropTypes.instanceOf(Goal),
};

export default AppSelection;
